import { PackagePlus, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  METHODES_PAIEMENT,
  METHODE_PAIEMENT_LABELS,
  calculerTotalVente,
  totalLigneVente,
  type MethodePaiement,
  type Product,
} from '@sirajoptic/shared';
import { Button } from '@/components/ui/button';
import { Combobox, type OptionCombobox } from '@/components/ui/combobox';
import { Dialog } from '@/components/ui/dialog';
import { InputField, SelectField } from '@/components/ui/field';
import { Encart } from '@/components/ui/feedback';
import { useToast } from '@/components/ui/toast';
import { ClientAutocomplete } from '@/features/clients/components/ClientAutocomplete';
import { ApiError } from '@/lib/api-client';
import { useProduitsPourSelection } from '@/features/stock/api/stock.api';
import { useAntiRebond } from '@/lib/anti-rebond';
import { dh, entier } from '@/lib/format';
import { useCreerVente } from '../api/ventes.api';

/** Une ligne du panier, telle qu'elle est en cours de saisie. */
type LignePanier = {
  cle: string;
  product_id: string | null;
  designation: string;
  /** Stock connu au moment de l'ajout — sert à prévenir avant l'envoi. */
  disponible: number | null;
  quantite: string;
  prix_unitaire: string;
};

function aujourdhui(): string {
  return new Date().toISOString().slice(0, 10);
}

function nouvelleCle(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function VenteDialog({ ouvert, onFermer }: { ouvert: boolean; onFermer: () => void }) {
  const [lignes, setLignes] = useState<LignePanier[]>([]);
  const [recherche, setRecherche] = useState('');
  const [methode, setMethode] = useState<MethodePaiement>('ESPECES');
  const [clientNom, setClientNom] = useState('');
  const [clientId, setClientId] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  /* Fixée à l'ouverture et conservée tant que la vente n'est pas passée : si le
   * premier envoi n'aboutit pas et que l'utilisateur reclique, le serveur
   * reconnaît la clé et rend la vente déjà enregistrée au lieu d'en créer une
   * seconde — avec une seconde sortie de stock. */
  const [cle, setCle] = useState('');

  /* Sans anti-rebond, chaque lettre balayait le stock. */
  const rechercheDifferee = useAntiRebond(recherche);
  const { data: produits, isFetching } = useProduitsPourSelection(rechercheDifferee);
  const creer = useCreerVente();
  const toast = useToast();

  useEffect(() => {
    if (!ouvert) return;
    setLignes([]);
    setRecherche('');
    setMethode('ESPECES');
    setClientNom('');
    setClientId(null);
    setErreur(null);
    setCle(`${Date.now().toString(36)}-${nouvelleCle()}`);
  }, [ouvert]);

  const options: OptionCombobox<Product>[] = (produits?.data ?? []).map((p) => ({
    cle: p.id,
    libelle: p.nom,
    description: `${entier(p.quantite)} en stock · ${dh(p.prix_vente)}`,
    valeur: p,
  }));

  function ajouterProduit(p: Product) {
    setLignes((l) => {
      // Reprendre la même référence incrémente plutôt que de doubler la ligne.
      const existante = l.find((x) => x.product_id === p.id);
      if (existante) {
        return l.map((x) =>
          x === existante ? { ...x, quantite: String(Number(x.quantite || '0') + 1) } : x,
        );
      }
      return [
        ...l,
        {
          cle: nouvelleCle(),
          product_id: p.id,
          designation: p.nom,
          disponible: p.quantite,
          quantite: '1',
          // Le prix affiché devient le prix de la vente, et reste modifiable.
          prix_unitaire: String(p.prix_vente),
        },
      ];
    });
    setRecherche('');
    setErreur(null);
  }

  function ajouterLigneLibre() {
    setLignes((l) => [
      ...l,
      {
        cle: nouvelleCle(),
        product_id: null,
        designation: '',
        disponible: null,
        quantite: '1',
        prix_unitaire: '',
      },
    ]);
  }

  function modifier(cleLigne: string, patch: Partial<LignePanier>) {
    setLignes((l) => l.map((x) => (x.cle === cleLigne ? { ...x, ...patch } : x)));
  }

  function retirer(cleLigne: string) {
    setLignes((l) => l.filter((x) => x.cle !== cleLigne));
  }

  const total = useMemo(
    () =>
      calculerTotalVente(
        lignes.map((l) => ({
          quantite: Number(l.quantite) || 0,
          prix_unitaire: Number(l.prix_unitaire) || 0,
        })),
      ),
    [lignes],
  );

  /* Prévenir avant l'envoi plutôt que de laisser le serveur refuser : le stock
   * connu ici peut être périmé, mais l'avertissement évite le plus courant. */
  const insuffisantes = lignes.filter(
    (l) => l.disponible !== null && Number(l.quantite) > l.disponible,
  );

  function valider(): boolean {
    if (lignes.length === 0) {
      setErreur('Ajoutez au moins un article.');
      return false;
    }
    for (const l of lignes) {
      if (l.designation.trim() === '') {
        setErreur('Chaque ligne libre doit porter une désignation.');
        return false;
      }
      if (!Number.isInteger(Number(l.quantite)) || Number(l.quantite) < 1) {
        setErreur(`Quantité invalide pour « ${l.designation || 'ligne sans nom'} ».`);
        return false;
      }
      if (l.prix_unitaire === '' || Number.isNaN(Number(l.prix_unitaire))) {
        setErreur(`Prix invalide pour « ${l.designation || 'ligne sans nom'} ».`);
        return false;
      }
    }
    setErreur(null);
    return true;
  }

  async function encaisser() {
    if (!valider()) return;

    try {
      const vente = await creer.mutateAsync({
        date_vente: aujourdhui(),
        lignes: lignes.map((l) => ({
          product_id: l.product_id,
          designation: l.designation.trim(),
          quantite: Number(l.quantite),
          prix_unitaire: Number(l.prix_unitaire),
          taux_tva: 0.2,
        })),
        methode,
        client_id: clientId,
        client_nom: clientNom.trim() || null,
        note: null,
        cle_idempotence: cle,
      });
      toast.succes(`Vente ${vente.numero} enregistrée — ${dh(vente.total)}.`);
      onFermer();
    } catch (e) {
      if (e instanceof ApiError) setErreur(e.message);
      else setErreur("La vente n'a pas pu être enregistrée.");
    }
  }

  return (
    <Dialog
      ouvert={ouvert}
      onFermer={onFermer}
      titre="Vente au comptoir"
      description="Encaissement immédiat. Les articles sortent du stock à la confirmation."
      taille="lg"
      modifie={lignes.length > 0 && !creer.isPending}
      pied={
        <>
          <span className="mr-auto text-sm text-ink-muted">
            {lignes.length === 0
              ? 'Panier vide'
              : `${entier(lignes.length)} ligne${lignes.length > 1 ? 's' : ''} · `}
            {lignes.length > 0 ? <strong className="tabular text-ink">{dh(total)}</strong> : null}
          </span>
          <Button variant="outline" onClick={onFermer} disabled={creer.isPending}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void encaisser()}
            chargement={creer.isPending}
            disabled={lignes.length === 0}
          >
            Encaisser {lignes.length > 0 ? dh(total) : ''}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-0 flex-1">
            <Combobox<Product>
              label="Ajouter un article"
              valeur={recherche}
              chargement={isFetching || recherche !== rechercheDifferee}
              options={options}
              placeholder="Rechercher dans le stock…"
              videMessage="Aucun produit trouvé"
              onChangeTexte={setRecherche}
              onSelectionner={(o) => ajouterProduit(o.valeur)}
            />
          </div>
          <Button
            variant="outline"
            onClick={ajouterLigneLibre}
            icone={<Plus aria-hidden className="h-4 w-4" />}
          >
            Ligne libre
          </Button>
        </div>

        {lignes.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-line px-4 py-10 text-center">
            <PackagePlus aria-hidden className="h-6 w-6 text-ink-muted" />
            <p className="text-sm text-ink-muted">
              Cherchez un article du stock, ou ajoutez une ligne libre pour ce qui n’y figure pas.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {lignes.map((l) => (
              <div
                key={l.cle}
                className="grid grid-cols-1 items-end gap-2 rounded-md border border-line p-3 sm:grid-cols-[1fr_88px_120px_auto]"
              >
                <InputField
                  label="Désignation"
                  value={l.designation}
                  onChange={(e) => modifier(l.cle, { designation: e.target.value })}
                  // Le nom d'un produit du stock est figé côté serveur de toute façon.
                  disabled={Boolean(l.product_id)}
                />
                <InputField
                  label="Qté"
                  type="number"
                  min={1}
                  value={l.quantite}
                  onChange={(e) => modifier(l.cle, { quantite: e.target.value })}
                  erreur={
                    l.disponible !== null && Number(l.quantite) > l.disponible
                      ? `${entier(l.disponible)} en stock`
                      : undefined
                  }
                />
                <InputField
                  label="Prix unitaire"
                  value={l.prix_unitaire}
                  onChange={(e) => modifier(l.cle, { prix_unitaire: e.target.value })}
                  aide={
                    Number(l.quantite) > 1
                      ? dh(totalLigneVente(Number(l.quantite) || 0, Number(l.prix_unitaire) || 0))
                      : undefined
                  }
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => retirer(l.cle)}
                  aria-label={`Retirer ${l.designation || 'cette ligne'}`}
                >
                  <Trash2 aria-hidden className="h-4 w-4 text-danger" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {insuffisantes.length > 0 ? (
          <Encart ton="moutarde" titre="Stock insuffisant d’après la fiche produit">
            La vente sera refusée pour {insuffisantes.map((l) => l.designation).join(', ')}. Si
            l’article est bien en rayon, corrigez le stock, ou passez-le en ligne libre.
          </Encart>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SelectField
            label="Règlement"
            value={methode}
            onChange={(e) => setMethode(e.target.value as MethodePaiement)}
          >
            {METHODES_PAIEMENT.map((m) => (
              <option key={m} value={m}>
                {METHODE_PAIEMENT_LABELS[m]}
              </option>
            ))}
          </SelectField>

          {/* Rattacher la vente à une fiche existante quand c'est possible :
              l'achat entre alors dans l'historique du client. Rester vide reste
              parfaitement valable — c'est le cas le plus courant au comptoir. */}
          <ClientAutocomplete
            label="Client"
            nom={clientNom}
            onChangeNom={(v) => {
              setClientNom(v);
              setClientId(null);
            }}
            onSelectionner={(c) => setClientId(c?.id ?? null)}
            aide={
              clientId
                ? "Rattachée à la fiche client — l'achat entre dans son historique."
                : 'Facultatif : laissez vide pour un client de passage.'
            }
          />
        </div>

        {erreur ? (
          <Encart ton="danger">
            <span role="alert">{erreur}</span>
          </Encart>
        ) : null}
      </div>
    </Dialog>
  );
}
